import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-navy mb-3">{title}</h2>
    <div className="text-navy/80 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Privacy = () => {
  return (
    <div className="bg-cream-light min-h-screen" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">Privacy Policy</h1>
        <p className="text-navy/60 text-sm mb-10">Last updated: June 2026</p>

        <Section title="Who we are">
          <p>
            Advectigroup (&ldquo;Advecti&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates a mobile advertising service in
            the Columbus, Ohio metropolitan area. Questions about this policy:{' '}
            <Link to="/contact" className="text-navy underline hover:text-gold">contact us here</Link>.
          </p>
        </Section>

        <Section title="What we collect">
          <p><strong>From advertisers:</strong> company name, contact name, email, phone number,
            campaign preferences, premium-zone coordinates, billing details (handled by our payment
            processors — we do not store full card numbers).</p>
          <p><strong>From drivers:</strong> name, email, phone, vehicle information, and GPS
            location/route data while a session is active.</p>
          <p><strong>From website visitors:</strong> standard server logs (IP address, browser type,
            pages requested, timestamps) for security and traffic analysis.</p>
          <p><strong>From integrations you connect:</strong> aggregated revenue and traffic data
            from Shopify, Square, and Stripe (only what you explicitly authorize via OAuth), plus
            anonymized site-visit counters from the Advecti Pixel if you choose to install it on
            your website.</p>
          <p><strong>From Instagram, if you connect your account:</strong> your Instagram
            professional account ID, username, account type, profile picture URL and public
            follower count; the list of your own posts (ID, caption, permalink, timestamp,
            thumbnail) so you can choose which post runs your giveaway; and the public comments on
            that chosen post, including each commenter&rsquo;s Instagram username, the comment text,
            the comment timestamp, and the usernames tagged in it. We do not request or receive
            your Instagram password, direct messages, private account data, email addresses of
            commenters, or the contents of any post you have not selected.</p>
        </Section>

        <Section title="How we use it">
          <p>To deliver, operate, bill, and improve the advertising service; to compute
            campaign analytics (impressions, lift); to send service notifications and
            optional weekly digests; to comply with legal obligations.</p>
        </Section>

        <Section title="Instagram data — how we use it and how to remove it">
          <p>
            Connecting Instagram is entirely optional and is only used to measure and run your
            wrap campaign&rsquo;s giveaway. Specifically, we use the data above to: take a daily
            snapshot of your follower count so you can see the growth your wrap produced; read the
            comments on the single post you designate so that entries can be recorded; count how
            many friends each entrant tagged; and select a winner at the end of each 30-day cycle.
          </p>
          <p>
            <strong>We never post, comment, message, follow, unfollow or change anything</strong> on
            your Instagram account. Our access is read-only apart from the webhook subscription
            that notifies us of new comments on your chosen post.
          </p>
          <p>
            <strong>We do not sell, rent or share Instagram data with any third party</strong>, and we
            do not use it for advertising targeting or to train any model. Entrant usernames are
            visible only to you and to Advecti staff administering the drawing.
          </p>
          <p>
            <strong>Retention and deletion.</strong> Access tokens are held server-side only, are
            never sent to your browser or exposed by any API response, and are deleted the moment
            you disconnect. You can disconnect Instagram at any time
            from the Social Attribution tab of your dashboard, which immediately deletes the stored
            access token and stops all data collection. To delete the Instagram data we have already
            collected — follower snapshots, recorded entries and comment records — use the delete
            option in your dashboard or{' '}
            <Link to="/contact" className="text-navy underline hover:text-gold">contact us</Link> and we
            will erase it within 30 days. You may also revoke our access directly from Instagram
            under Settings &rarr; Website Permissions &rarr; Apps and Websites.
          </p>
          <p>
            Giveaways run through this feature are in no way sponsored, endorsed, administered by, or
            associated with Instagram or Meta Platforms, Inc.
          </p>
        </Section>

        <Section title="Who we share it with">
          <p>We do not sell personal information. We share data only with service providers
            that help us operate the platform: Stripe (payments), Shopify/Square (your own
            data, only with your OAuth consent), our hosting provider, and our email delivery
            provider. Each is contractually required to protect your data.</p>
        </Section>

        <Section title="Cookies & local storage">
          <p>We use browser local storage to keep you logged in (a session token), and the
            Advecti Pixel writes a hashed visitor identifier (IP + user-agent) on advertiser
            websites that install it, used solely to count unique daily visitors. We do not
            use third-party advertising cookies, Facebook Pixel, or other cross-site trackers
            on this site. If we add them later, we will update this policy and add a consent
            prompt where required.</p>
        </Section>

        <Section title="Your choices">
          <p><strong>Access, correction:</strong> email us and we will respond within 30 days.</p>
          <p><strong>Disconnect integrations:</strong> log in to your dashboard, open the
            <em> Integrations</em> tab, and click <em>Disconnect</em> on any connected service
            (Shopify, Square, Stripe, or Manual Entry). We revoke the OAuth
            token immediately and stop pulling new data from that source.</p>
          <p><strong>Delete everything (right-to-erasure):</strong> log in to your dashboard,
            open the <em>Privacy &amp; Data</em> tab, and click <em>Permanently delete my account</em>.
            We will erase your advertiser record, campaigns, premium zones, driver-session
            history, conversion entries, connected-integration tokens, and uploaded files
            from our production database. This action is immediate and irreversible.
            If your campaign is still active when you delete, an early-termination penalty
            applies &mdash; see the <Link to="/terms" className="text-navy underline">Terms of Service</Link>
            {' '}for details. <strong>Disconnecting individual integrations does NOT trigger
            a penalty</strong>; only full account deletion does.</p>
          <p><strong>California residents (CCPA):</strong> you also have the right to know what
            we collect, request deletion (above), and opt out of any &ldquo;sale&rdquo; of personal information &mdash;
            we do not sell. <strong>EU/UK residents:</strong> we honor GDPR rights including data
            portability and the right to object. To exercise data portability, email us and we
            will deliver a machine-readable export within 30 days.</p>
        </Section>

        <Section title="How long we keep it">
          <p>Account and campaign data: while your account is active plus seven years for
            tax and compliance records. Driver GPS routes: aggregated indefinitely; the raw
            session log is retained for two years. Server logs: 90 days.</p>
        </Section>

        <Section title="Security">
          <p>We use TLS in transit, encrypted storage at rest, and least-privilege access
            controls. No system is perfectly secure; please use a strong password.</p>
        </Section>

        <Section title="Children">
          <p>Advecti is not directed to anyone under 13 and we do not knowingly collect data
            from them.</p>
        </Section>

        <Section title="Changes">
          <p>We may update this policy. Material changes will be flagged at the top of this
            page. Continued use of the service after changes constitutes acceptance.</p>
        </Section>

        <p className="text-navy/50 text-xs mt-10">
          This summary is provided in plain English for clarity. It is not a substitute for
          legal advice.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
